import { AnimatePresence, motion, Variants } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useModal, useViewport } from "src/contexts";
import { ConfiguratorContext, useConfiguratorContext } from "src/pages/configurator/Configurator.context";
import { PDFPrintModal } from "src/pages/configuratorPrint/PDFPrintModal";
import { createConfigurationsDownloadUrl, createConfigurationsLandingUrl } from "src/RouteUrls";
import { useCurrentUser } from "src/utils/hooks/useCurrentUser";
import { legacyHasSelectedAduOption } from "src/utils/summary.utils";
import { Button, IconButton } from "~components";
import { Css } from "~generated/css";
import { CheckoutConfigStatus } from "~generated/graphqlTypes";
import { defaultTestId, safeKeys, titleCase, useTestIds } from "~utils";
import { EXPIRATION_DAYS } from "../stepTemplates";
import { FlowStep, FlowSteps } from "../stepTemplates/FlowSteps";
import { ConfigurationHeader_CheckoutConfigFragment } from "./ConfigurationHeader.generated";
import { DebugModal } from "./DebugModal";
import { ModalWrapper } from "./ModalWrapper";
import { ReserveYourBuildFlyout } from "./ReserveYourBuildFlyout";
import { VersionBadge } from "./VersionBadge";
import { WarningModal } from "./WarningModal";

const SubMenuContainerVariant: Variants = {
  closed: {
    height: 0,
    opacity: 0,
    transition: {
      staggerChildren: 0,
      delayChildren: 0,
    },
  },
  open: {
    height: "auto",
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
      bounce: 0,
    },
  },
};

const SubMenuItemVariant: Variants = {
  closed: {
    opacity: 0,
    y: -10,
    transition: {
      duration: 0.1,
    },
  },
  open: {
    opacity: 1,
    y: 0,
  },
};

export const HeaderHeight = 64;
// Export so mobile stepwrapper can correctly pin content to the top of screen without overlapping the header
export const MobileHeaderId = "mobile-configuration-header";

export function ConfiguratorHeader() {
  const { isMobile } = useViewport();
  return isMobile ? <MobileHeader /> : <DesktopHeader />;
}

function MobileHeader() {
  const configuratorContext = useConfiguratorContext();
  const { goToStep, currentFlowStep, checkoutConfig, communityId, hasAduSupport } = configuratorContext;
  const navigateWithWarning = useStepNavigationWithWarning();
  const { subStep: urlSubStep } = useParams<{ subStep?: string }>();
  const navigate = useNavigate();
  // Use -1 to indicate no sub-step is selected so title resets to current step
  const subStep = useMemo(() => parseInt(urlSubStep ?? "-1") ?? -1, [urlSubStep]);

  const [menuOpen, setMenuOpen] = useState(false);
  const [interiorExpanded, setInteriorExpanded] = useState(false);
  const steps = hasAduSupport ? safeKeys(FlowSteps) : safeKeys(FlowSteps).filter((step) => step !== FlowStep.Adu);
  const lastEnabledHeaderStepIndex = getLastEnabledHeaderStepIndex(configuratorContext);

  const interiorSubSteps = checkoutConfig?.interiorSubSteps ?? [];
  const currentSubStep = interiorSubSteps.find(({ stepNumber }) => stepNumber === subStep)?.displayName;

  useEffect(() => {
    if (menuOpen && currentFlowStep === FlowStep.Interior && !interiorExpanded) {
      setInteriorExpanded(true);
    } else if (!menuOpen) {
      setInteriorExpanded(false);
    }
  }, [menuOpen, currentFlowStep, subStep, interiorExpanded]);

  return (
    <>
      {/* Logo | Page title (currentStep) | x of y steps */}
      <header
        id={MobileHeaderId}
        // Using a z2 (same for stepper) to make sure anything within stepwrapper doesnt cover us here
        css={Css.z2.w100.df.asStretch.px2.py1.fs0.aic.bgWhite.sticky.top0.$}
      >
        <div css={Css.df.$}>
          <img width="37" height="24" src="/mobile-logo.svg" alt="Homebound" />
        </div>
        <div css={Css.df.f1.px2.t24.$}>
          {currentFlowStep === FlowStep.Adu ? "ADU" : titleCase(currentSubStep ?? currentFlowStep)}
        </div>
        {/* Only open once we're loaded so links dont show in a broken state */}
        <div css={Css.df.t12.gray.if(!!checkoutConfig).tdu.$} onClick={() => !!checkoutConfig && setMenuOpen(true)}>
          Step {steps.indexOf(currentFlowStep) + 1} of {steps.length}
        </div>
      </header>
      {/* Modal Menu containing all steps */}
      <ModalWrapper isOpen={menuOpen} close={() => setMenuOpen(false)}>
        <div css={Css.df.fdc.gap2.px2.f1.oh.relative.$}>
          <div css={Css.df.fdc.oa.f1.$}>
            {steps.map((flowStep, idx) => {
              const hasSubSteps = flowStep === FlowStep.Interior && interiorSubSteps.length > 0;
              const isActiveStep = flowStep === currentFlowStep;
              const isDisabled = idx > lastEnabledHeaderStepIndex;

              return (
                <div key={flowStep}>
                  <div
                    css={Css.df.gap4.py3.aic.cursorPointer.if(isDisabled).gray300.$}
                    onClick={() => {
                      if (isDisabled) return;
                      if (hasSubSteps) {
                        setInteriorExpanded(!interiorExpanded);
                      } else {
                        navigateWithWarning(flowStep);
                        setMenuOpen(false);
                      }
                    }}
                  >
                    <div css={Css.t16.gray.$}>{idx + 1}</div>
                    <div css={Css.df.t24.gap2.if(isActiveStep).brightYellow.$}>
                      {flowStep === FlowStep.Adu ? "ADU" : titleCase(flowStep)}
                    </div>
                  </div>
                  {/* Interior sub-step menu */}
                  <AnimatePresence>
                    {hasSubSteps && interiorExpanded && (
                      <motion.div
                        variants={SubMenuContainerVariant}
                        initial="closed"
                        animate="open"
                        exit="closed"
                        // pixel perfect left padding to align with parent
                        css={Css.df.fdc.plPx(44).gap3.$}
                      >
                        {interiorSubSteps.map((subStepItem) => {
                          const isActiveSubStep = isActiveStep && subStep === subStepItem.stepNumber;
                          return (
                            <motion.div
                              key={subStepItem.id}
                              id={`interior-sub-step-${subStepItem.stepNumber}`}
                              variants={SubMenuItemVariant}
                              css={Css.df.t18.gray.if(isActiveSubStep).brightYellow.$}
                              onClick={() => {
                                goToStep(FlowStep.Interior, `${subStepItem.stepNumber}`);
                                setMenuOpen(false);
                              }}
                            >
                              {subStepItem.displayName}
                            </motion.div>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
          <div css={Css.sticky.hPx(94).df.aic.jcc.bottom0.$}>
            <div>
              {/* Note: No mention of showing the flyout here since we're assuming mobile is still walking the home */}
              <Button variant="inline" xss={Css.t18.black.$} onClick={() => navigate(createConfigurationsLandingUrl())}>
                View Configurations
              </Button>
            </div>
          </div>
        </div>
      </ModalWrapper>
    </>
  );
}

function DesktopHeader() {
  const configuratorContext = useConfiguratorContext();
  const { currentFlowStep, checkoutConfig, hasAduSupport } = configuratorContext;
  // Narrow to our component-specific fragment
  const config: ConfigurationHeader_CheckoutConfigFragment | undefined = checkoutConfig;
  const steps = hasAduSupport ? safeKeys(FlowSteps) : safeKeys(FlowSteps).filter((step) => step !== FlowStep.Adu);
  const navigate = useNavigate();
  const navigateWithWarning = useStepNavigationWithWarning();
  const { currentUser, isHomeowner } = useCurrentUser();
  const currentUserEmail = currentUser?.email;
  const currentUserName = currentUser?.name;
  const { showModal, closeModal } = useModal();

  const tid = useTestIds({}, "header");

  const lastEnabledHeaderStepIndex = getLastEnabledHeaderStepIndex(configuratorContext);

  const [showReserveFlyout, setShowReserveFlyout] = useState(false);

  // We only want to show the save button if a user is "in progress" of creating a build past the community/lot step
  // (i.e. not reserved or expired, not on summary step, not on community step, not on lot step)
  const shouldShowSaveButton =
    !isHomeowner && // Only show save button for sales reps to key in personal info, we'll already have it for homeowners
    checkoutConfig?.status === CheckoutConfigStatus.Draft &&
    currentFlowStep !== FlowStep.Summary &&
    currentFlowStep !== FlowStep.Community &&
    currentFlowStep !== FlowStep.Lot;

  return (
    <header css={Css.z1.hPx(HeaderHeight).w100.df.asfs.px3.fs0.$}>
      <div css={Css.w100.df.aic.jcsb.relative.$}>
        <div css={Css.df.aic.gap2.$}>
          <img width="134" height="26" src="/logo.svg" alt="Homebound" />
          {/* Left of the steps, not by the debug button: the centered step nav leaves the right side far less
              room, i.e. the full badge collides there below ~1620px vs ~1380px here */}
          {config && <VersionBadge checkoutConfig={config} />}
        </div>

        {/* Only show step shortcuts when edit is still allowed */}
        {!checkoutConfig?.reservedAt && (
          <div css={Css.absolute.left("50%").tx("-50%").df.jcse.wPx(734).t14.h100.aic.$}>
            {steps.map((flowStep, i) => {
              return (
                <NavStep
                  key={flowStep}
                  menuTitle={flowStep}
                  isActive={flowStep === currentFlowStep}
                  appendSlash={i !== steps.length - 1}
                  onClick={() => navigateWithWarning(flowStep)}
                  isDisabled={i > lastEnabledHeaderStepIndex}
                />
              );
            })}
          </div>
        )}

        <div css={Css.df.gap1.$}>
          <div css={Css.df.aic.gap1.$}>
            {/* Internal-only "Download PDF" report-picker modal, summary step only */}
            {currentFlowStep === FlowStep.Summary && !isHomeowner && checkoutConfig && (
              <IconButton
                size="small"
                icon="document"
                onClick={() =>
                  showModal(
                    <PDFPrintModal
                      onClose={closeModal}
                      checkoutConfig={checkoutConfig}
                      allowAduReport={
                        hasAduSupport &&
                        (!!checkoutConfig.aduProductOffering || legacyHasSelectedAduOption(checkoutConfig.optionGroups))
                      }
                      onContinue={(reports, includePricing, additionalSitePriceInCents) =>
                        window.open(
                          createConfigurationsDownloadUrl(
                            checkoutConfig.id,
                            reports,
                            includePricing,
                            additionalSitePriceInCents,
                          ),
                          "_blank",
                        )
                      }
                    />,
                  )
                }
                {...tid.downloadPdf_button}
              />
            )}
            {/* There is a feature flag guarding the `pricingTraceUrl` field, so we'll use it here to block access to the debug modal */}
            {checkoutConfig?.pricingTraceUrl && (
              <div css={Css.df.aic.gap1.$}>
                <IconButton
                  size="small"
                  onClick={() => showModal(<DebugModal checkoutConfigId={checkoutConfig.id} onClose={closeModal} />)}
                  icon="chart-candle"
                  {...tid.debug_button}
                />
              </div>
            )}
            <Button variant="secondary" onClick={() => navigate(createConfigurationsLandingUrl())}>
              Configurations
            </Button>
          </div>
          {!!config?.reservedAt ? (
            <div css={Css.df.aic.gap3.pr2.$}>
              {config.name && (
                <div css={Css.gray800.fw200.$}>
                  Build: <span css={Css.gray800.fw500.$}>{config.name}</span>
                </div>
              )}
              <div css={Css.gray800.fw200.$}>
                Reserved:{" "}
                <span css={Css.gray800.fw500.$} {...tid.reservedAt}>
                  {new Date(config.reservedAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              </div>
              {config.status === CheckoutConfigStatus.Expired && (
                <div css={Css.gray800.fw200.pr2.$}>
                  Expired:{" "}
                  <span css={Css.gray800.fw500.$} {...tid.expiresAt}>
                    {new Date(config.expiresAt!).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <>
              {shouldShowSaveButton && (
                <div css={Css.df.aic.$}>
                  <Button variant="secondary" outline="black" onClick={() => setShowReserveFlyout(true)}>
                    Save
                  </Button>
                </div>
              )}
              {showReserveFlyout && checkoutConfig && (
                <ReserveYourBuildFlyout
                  onClose={() => setShowReserveFlyout(false)}
                  checkoutConfig={checkoutConfig}
                  expirationInDays={EXPIRATION_DAYS}
                  currentUserEmail={currentUserEmail}
                  currentUserName={currentUserName}
                  isHomeowner={isHomeowner}
                />
              )}
            </>
          )}
        </div>
      </div>
    </header>
  );
}

type NavStepProps = {
  menuTitle: string;
  isActive: boolean;
  appendSlash?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
};

/**
 *
 * Used in desktop header and mobile sub menus (camera/room switchers)
 *
 * Name matches figma label
 * */
export function NavStep(props: NavStepProps) {
  const { menuTitle, isActive, appendSlash = false, onClick, isDisabled = false } = props;
  const tid = useTestIds(props, "header-step");
  return (
    <>
      <div
        css={{
          ...Css.w100.h100.df.fg1.px2.fdc.aic.bb.bw("3px").bcTransparent.if(isActive).bcBrightYellow.$,
          ...Css.ifMobile.wsnw.$,
        }}
      >
        <button
          css={{
            ...Css.cursorPointer.df.h100.aic.t14.gray.$,
            ...Css.if(isActive).gray800.$,
            ...Css.if(!isActive && !isDisabled).onHover.color("rgba(53, 53, 53, 0.80)").$,
            ...Css.if(isDisabled).gray300.cursorNotAllowed.$,
            // Must show `Adu` as `ADU`
            ...Css.if(menuTitle === FlowStep.Adu).ttu.else.ttc.$,
          }}
          onClick={onClick}
          disabled={isDisabled}
          {...tid[`${defaultTestId(menuTitle)}`]}
        >
          <span>{menuTitle}</span>
        </button>
      </div>
      {appendSlash && <span css={Css.t14.gray.if(isDisabled).gray300.$}>/</span>}
    </>
  );
}

function getLastEnabledHeaderStepIndex(configuratorContext: ConfiguratorContext): number {
  const { communityId, checkoutConfig, lotId, hasDirtyPlanSelection } = configuratorContext;
  if (!communityId) {
    return 0;
  } else if (!checkoutConfig && !lotId) {
    return 1;
  } else if (!checkoutConfig || hasDirtyPlanSelection) {
    // Community + Lot + Plan enabled — either pre-config (no checkoutConfig yet)
    // or the user has unsaved plan changes that need to be committed via "Next"
    return 2;
  } else {
    // Once we have a checkoutConfig, it's initialized from either a valid base POFConfig or the Structures
    // current set of options, so there is no reason to disable any steps (i.e. block the Options step on
    // having the required Exterior/Interior options selected--they'll already be selected).
    return Object.keys(FlowSteps).length;
  }
}

// Display a warning when users navigate back to Community/Plan steps during config editing
// This will create a new configuration entirely, so we need to warn users about data loss
function useStepNavigationWithWarning() {
  const { showModal, closeModal } = useModal();
  const { goToStep, checkoutConfig } = useConfiguratorContext();

  const navigateWithWarning = (flowStep: FlowStep) => {
    const requiresNewConfig = flowStep === FlowStep.Community || flowStep === FlowStep.Lot;

    if (checkoutConfig?.id && requiresNewConfig) {
      showModal(
        <WarningModal
          title="Are you sure?"
          confirmationMessage={`Selecting a new ${flowStep} will require an entirely new configuration.`}
          onContinue={() => goToStep(flowStep, FlowSteps[flowStep].hasSubSteps ? "1" : undefined)}
          onClose={closeModal}
        />,
      );
    } else {
      goToStep(flowStep, FlowSteps[flowStep].hasSubSteps ? "1" : undefined);
    }
  };

  return navigateWithWarning;
}
